import { useState } from 'react';
import type { JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { EmptyState, ErrorState, LoadingState, Panel, StatusBadge } from '../components/ui.js';

interface Row {
  id: string;
  name: string;
  type: string | null;
  protected: boolean;
}

/**
 * Seznamy ve SmartEmailingu, do kterých hub nikdy nezapisuje: přidání kontaktu do seznamu spouští automatizace
 * (uvítací slevy, kampaně). Hub navíc zapisuje jen do technických spouštěčů (`JRN_*`); tady lze další seznamy
 * výslovně zakázat.
 */
export function ProtectedLists({ canWrite }: { canWrite: boolean }): JSX.Element {
  const client = useQueryClient();
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ['se-protected', q],
    queryFn: () =>
      api.get<{ protectedCount: number; items: Row[] }>(
        `/api/se-protected-lists?q=${encodeURIComponent(q)}`,
      ),
  });
  const save = useMutation({
    mutationFn: (ids: string[]) => api.put('/api/se-protected-lists', { ids }),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['se-protected'] });
    },
    onError: (e: Error) => setError(e.message),
  });
  const toggle = (row: Row): void => {
    const current = (query.data?.items ?? []).filter((i) => i.protected).map((i) => i.id);
    const next = row.protected ? current.filter((id) => id !== row.id) : [...current, row.id];
    save.mutate(next);
  };
  return (
    <Panel
      title="Seznamy, do kterých hub nezapisuje"
      subtitle="Přidání kontaktu do seznamu ve SmartEmailingu spouští automatizace (uvítací slevy, kampaně). Hub zapisuje jen do technických spouštěčů JRN_*; sem označte další seznamy, které nesmí nikdy změnit."
    >
      <input
        value={q}
        placeholder="hledat seznam podle názvu (např. sleva, Monza)"
        aria-label="Hledat seznam"
        onChange={(e) => setQ(e.target.value)}
      />
      {query.isLoading ? <LoadingState what="seznamy" /> : null}
      {query.isError ? (
        <ErrorState title="Seznamy se nepodařilo načíst" error={query.error} />
      ) : null}
      {query.data ? (
        <>
          <p className="note">Chráněných seznamů: {query.data.protectedCount}.</p>
          {query.data.items.length === 0 ? (
            <EmptyState title="Žádný seznam nenalezen">
              Seznamy se načtou synchronizací ze SmartEmailingu.
            </EmptyState>
          ) : (
            <div className="scroll">
              <table>
                <thead>
                  <tr>
                    <th>Seznam</th>
                    <th>Třída</th>
                    <th>Stav</th>
                  </tr>
                </thead>
                <tbody>
                  {query.data.items.map((r) => (
                    <tr key={r.id}>
                      <td>
                        {r.name} <small className="muted">#{r.id}</small>
                      </td>
                      <td>{r.type ?? '—'}</td>
                      <td>
                        {canWrite ? (
                          <button
                            type="button"
                            className={`btn${r.protected ? '' : ' pri'}`}
                            disabled={save.isPending}
                            onClick={() => toggle(r)}
                          >
                            {r.protected ? 'Povolit zápis' : 'Chránit před zápisem'}
                          </button>
                        ) : r.protected ? (
                          <StatusBadge tone="warn" icon="alert">
                            chráněný
                          </StatusBadge>
                        ) : null}
                        {r.protected && canWrite ? (
                          <StatusBadge tone="warn" icon="alert">
                            chráněný
                          </StatusBadge>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}
      {error ? <ErrorState title="Uložení se nepovedlo" error={new Error(error)} /> : null}
    </Panel>
  );
}
